/**
 * b2bInviteService.js
 * 
 * Gestiona el Deep Linking B2B para la plataforma WE360.
 * Captura parámetros de invitación corporativa (?cid=, ?empresa=, ?email=)
 * desde cualquier punto de entrada y los enlaza de forma transparente
 * al perfil del usuario en Firestore.
 */

import { db, auth } from "@/firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";

const STORAGE_KEY_CID = "we360_b2b_cid";
const STORAGE_KEY_EMAIL = "we360_invited_email";

/**
 * Captura los parámetros de invitación de la URL y los guarda en sessionStorage y localStorage.
 * @returns {{ cid: string|null, email: string|null }}
 */
export function captureInviteParams() {
  if (typeof window === "undefined") return { cid: null, email: null };
  try {
    const params = new URLSearchParams(window.location.search);
    const cid = (params.get("cid") || params.get("empresa") || params.get("company_id") || "").trim();
    const email = (params.get("email") || "").trim().toLowerCase();

    if (cid) {
      sessionStorage.setItem(STORAGE_KEY_CID, cid);
      localStorage.setItem(STORAGE_KEY_CID, cid);
    }
    if (email) {
      sessionStorage.setItem(STORAGE_KEY_EMAIL, email);
      localStorage.setItem(STORAGE_KEY_EMAIL, email);
    }

    return {
      cid: cid || getSavedB2BInvite().cid,
      email: email || getSavedB2BInvite().email
    };
  } catch (err) {
    console.warn("[b2bInviteService] Error capturando parámetros de invitación:", err);
    return { cid: null, email: null };
  }
}

/**
 * Obtiene los datos de la invitación B2B guardada en sesión/almacenamiento local.
 * @returns {{ cid: string|null, email: string|null }}
 */
export function getSavedB2BInvite() {
  if (typeof window === "undefined") return { cid: null, email: null };
  try {
    const cid = sessionStorage.getItem(STORAGE_KEY_CID) || localStorage.getItem(STORAGE_KEY_CID) || null;
    const email = sessionStorage.getItem(STORAGE_KEY_EMAIL) || localStorage.getItem(STORAGE_KEY_EMAIL) || null;
    return { cid, email };
  } catch {
    return { cid: null, email: null };
  }
}

/**
 * Limpia los parámetros de invitación de almacenamiento.
 */
export function clearSavedB2BInvite() {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(STORAGE_KEY_CID);
    localStorage.removeItem(STORAGE_KEY_CID);
    sessionStorage.removeItem(STORAGE_KEY_EMAIL);
    localStorage.removeItem(STORAGE_KEY_EMAIL);
  } catch {}
}

/**
 * Obtiene el nombre legible de una empresa a partir de su ID.
 * @param {string} clientId
 * @returns {Promise<string>}
 */
export async function fetchCompanyName(clientId) {
  if (!clientId) return "";
  try {
    if (auth.currentUser) {
      const clientRef = doc(db, "clients", clientId);
      const clientSnap = await getDoc(clientRef);
      if (clientSnap.exists()) {
        return clientSnap.data().name || clientId;
      }
    }
  } catch (err) {
    console.warn("[b2bInviteService] Error al obtener nombre de empresa:", err);
  }
  return clientId;
}

/**
 * Vincula de forma transparente al usuario con la empresa en Firestore.
 * @param {string} uid
 * @param {string} email
 * @param {string|null} existingClientId
 * @returns {Promise<string|null>}
 */
export async function linkUserToB2BCompany(uid, email, existingClientId = null) {
  if (!uid || existingClientId) return existingClientId;

  try {
    const { cid: savedCid } = getSavedB2BInvite();
    if (!savedCid) return null;

    const companyName = await fetchCompanyName(savedCid);

    const userRef = doc(db, "users", uid);
    await setDoc(userRef, {
      clientId: savedCid,
      empresa: companyName,
      isB2B: true,
      clientRole: "member",
      onboardingStep: "scan_pending",
      linkedVia: "deep_linking",
      linkedAt: new Date().toISOString(),
    }, { merge: true });

    clearSavedB2BInvite();
    return savedCid;
  } catch (err) {
    console.error("[b2bInviteService] Error vinculando usuario a la empresa:", err);
    return null;
  }
}
